import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Users, Heart, TrendingUp, Calendar, Sparkles } from 'lucide-react';
import { usePartnerLink } from '@/hooks/usePartnerLink';
import { useRelationshipAssessment } from '@/hooks/useRelationshipAssessment';
import { getScoreInterpretation } from '@/data/relationshipAssessmentQuestions';
import { CouplesReflectionSession } from './CouplesReflectionSession';
import { format, parseISO } from 'date-fns';

interface PartnerAssessmentViewProps {
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  userAssessment?: any;
}

export function PartnerAssessmentView({ relationshipType, userAssessment }: PartnerAssessmentViewProps) {
  const [showReflection, setShowReflection] = useState(false);
  const { partnerLink, isLinked } = usePartnerLink();
  const partnerId = isLinked && partnerLink 
    ? (partnerLink.partner_id === partnerLink.requester_id ? partnerLink.partner_id : partnerLink.partner_id)
    : undefined;
  
  const { assessments: partnerAssessments, isLoading } = useRelationshipAssessment(partnerId);

  if (!isLinked || !partnerLink) {
    return null;
  }

  const latestPartnerAssessment = partnerAssessments.length > 0 ? partnerAssessments[0] : null;

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="h-24" />
      </Card>
    );
  }

  if (!latestPartnerAssessment) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {partnerLink.partner_display_name} hasn't completed an assessment yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const healthScore = latestPartnerAssessment.relationship_health_score || 0;
  const interpretation = getScoreInterpretation(Math.round(healthScore / 100 * 21));

  return (
    <>
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-medium">
                {partnerLink.partner_display_name}'s View
              </CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              {format(parseISO(latestPartnerAssessment.assessment_date), 'MMM d')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: interpretation.color }}>
                {Math.round(healthScore)}%
              </p>
              <p className="text-xs text-muted-foreground">
                {interpretation.label}
              </p>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>Compare perspectives</p>
              <p>to grow together</p>
            </div>
          </div>

          {/* AI Reflection Session Button */}
          <Dialog open={showReflection} onOpenChange={setShowReflection}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full gap-2 bg-gradient-to-r from-secondary/10 to-primary/10 border-secondary/20 hover:border-secondary/40"
              >
                <Sparkles className="w-4 h-4 text-secondary" />
                Start AI Couples Reflection
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
              <CouplesReflectionSession
                userAssessment={userAssessment}
                partnerAssessment={latestPartnerAssessment}
                partnerName={partnerLink.partner_display_name}
                onClose={() => setShowReflection(false)}
              />
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </>
  );
}
